import { format } from 'date-fns';
import { DataType, DateTime } from '@mode-switch/express';
import { ChartTypes } from '@mode/shared/contract-common';
import { getDefaultTextFormat, getFormatFromShortcut, getTextFormatOptionsByShortcutType } from '@mode/shared/util-js';

function getTextFormatProps(
  shortcutType: ChartTypes.FormatShortcutType,
  configType = shortcutType
): ChartTypes.TextFormat {
  return {
    ...getTextFormatOptionsByShortcutType(configType),
    type: getFormatFromShortcut(configType),
    shortcutType,
  };
}

export interface ShortcutOption {
  label: string;
  example?: string;
  disabled?: boolean;
  shortcut?: {
    type: ChartTypes.FormatShortcutType;
    format: ChartTypes.TextFormat;
  };
}

/**
 * Unsupported Data types [and DATE_PARTS] do not have formatting options
 */
export const plainTextFormatOptions: ShortcutOption[] = [
  {
    label: 'Plain text',
    disabled: true,
  },
];

const NOW = new Date();
const formatMonYear = format(NOW, 'MMM yyyy');
const formatYearMonDate = format(NOW, 'yyyy-MM-d');
const formatDateAndTime = format(NOW, 'yyyy-MM-d hh:mm:ss');
const formatMonthDay = format(NOW, 'MM/d');
const formatYear = format(NOW, 'yyyy');

/**
 * Format shortcut options for Date like data types
 */
export const dateFormatOptions: ShortcutOption[] = [
  {
    label: 'Auto',
    shortcut: {
      type: ChartTypes.FormatShortcutType.Automatic,
      format: getTextFormatProps(ChartTypes.FormatShortcutType.Automatic, ChartTypes.FormatShortcutType.DateAndTime),
    },
  },
  {
    label: 'Mon Year',
    example: formatMonYear,
    shortcut: {
      type: ChartTypes.FormatShortcutType.MonthYear,
      format: getTextFormatProps(ChartTypes.FormatShortcutType.MonthYear),
    },
  },
  {
    label: 'Year-Mon-Date',
    example: formatYearMonDate,
    shortcut: {
      type: ChartTypes.FormatShortcutType.YearMonthDate,
      format: getTextFormatProps(ChartTypes.FormatShortcutType.YearMonthDate),
    },
  },
  {
    label: 'Date and Time',
    example: formatDateAndTime,
    shortcut: {
      type: ChartTypes.FormatShortcutType.DateAndTime,
      format: getTextFormatProps(ChartTypes.FormatShortcutType.DateAndTime),
    },
  },
  {
    label: 'Mon/Day',
    example: formatMonthDay,
    shortcut: {
      type: ChartTypes.FormatShortcutType.MonthDay,
      format: getTextFormatProps(ChartTypes.FormatShortcutType.MonthDay),
    },
  },
  {
    label: 'Year',
    example: formatYear,
    shortcut: {
      type: ChartTypes.FormatShortcutType.Year,
      format: getTextFormatProps(ChartTypes.FormatShortcutType.Year),
    },
  },
];

/**
 * Format shortcut options for Numeric data types
 */
export const numberFormatOptions: ShortcutOption[] = [
  {
    label: 'Auto',
    shortcut: {
      type: ChartTypes.FormatShortcutType.Automatic,
      format: {
        ...getDefaultTextFormat(DataType.Name.Float),
      },
    },
  },
  {
    label: 'Short number',
    example: '1,006',
    shortcut: {
      type: ChartTypes.FormatShortcutType.ShortNumber,
      format: getTextFormatProps(ChartTypes.FormatShortcutType.ShortNumber),
    },
  },
  {
    label: 'Long number',
    example: '1,006.00',
    shortcut: {
      type: ChartTypes.FormatShortcutType.LongNumber,
      format: getTextFormatProps(ChartTypes.FormatShortcutType.LongNumber),
    },
  },
  {
    label: 'Percent',
    example: '10.62%',
    shortcut: {
      type: ChartTypes.FormatShortcutType.Percent,
      format: getTextFormatProps(ChartTypes.FormatShortcutType.Percent),
    },
  },
  {
    label: 'Currency',
    example: '$1,600',
    shortcut: {
      type: ChartTypes.FormatShortcutType.Currency,
      format: getTextFormatProps(ChartTypes.FormatShortcutType.Currency),
    },
  },
];

export const datePartYearFormatOptions: ShortcutOption[] = [
  {
    label: 'Four digits (default)',
    example: formatYear,
    shortcut: {
      type: ChartTypes.FormatShortcutType.Automatic,
      format: {
        ...getTextFormatProps(ChartTypes.FormatShortcutType.Automatic, ChartTypes.FormatShortcutType.FullName),
      },
    },
  },
  {
    label: 'Two digits',
    example: format(NOW, 'yy'),
    shortcut: {
      type: ChartTypes.FormatShortcutType.ShortName,
      format: getTextFormatProps(ChartTypes.FormatShortcutType.ShortName),
    },
  },
];

export const datePartQuarterFormatOptions: ShortcutOption[] = [
  {
    label: 'Abbreviated (default)',
    example: format(NOW, 'QQQ'),
    shortcut: {
      type: ChartTypes.FormatShortcutType.Automatic,
      format: {
        ...getTextFormatProps(ChartTypes.FormatShortcutType.Automatic, ChartTypes.FormatShortcutType.ShortName),
      },
    },
  },
  {
    label: 'Full name',
    example: format(NOW, 'QQQQ'),
    shortcut: {
      type: ChartTypes.FormatShortcutType.FullName,
      format: getTextFormatProps(ChartTypes.FormatShortcutType.FullName),
    },
  },
  {
    label: 'Ordinal',
    example: format(NOW, 'Qo'),
    shortcut: {
      type: ChartTypes.FormatShortcutType.Ordinal,
      format: getTextFormatProps(ChartTypes.FormatShortcutType.Ordinal),
    },
  },
  {
    label: 'Numeric',
    example: format(NOW, 'Q'),
    shortcut: {
      type: ChartTypes.FormatShortcutType.Numeric,
      format: getTextFormatProps(ChartTypes.FormatShortcutType.Numeric),
    },
  },
];

export const datePartMonthFormatOptions: ShortcutOption[] = [
  {
    label: 'Full name (default)',
    example: format(NOW, 'MMMM'),
    shortcut: {
      type: ChartTypes.FormatShortcutType.Automatic,
      format: {
        ...getTextFormatProps(ChartTypes.FormatShortcutType.Automatic, ChartTypes.FormatShortcutType.FullName),
      },
    },
  },
  {
    label: 'Abbreviated',
    example: format(NOW, 'MMM'),
    shortcut: {
      type: ChartTypes.FormatShortcutType.ShortName,
      format: getTextFormatProps(ChartTypes.FormatShortcutType.ShortName),
    },
  },
  {
    label: 'Ordinal',
    example: format(NOW, 'Mo'),
    shortcut: {
      type: ChartTypes.FormatShortcutType.Ordinal,
      format: getTextFormatProps(ChartTypes.FormatShortcutType.Ordinal),
    },
  },
  {
    label: 'Numeric',
    example: format(NOW, 'M'),
    shortcut: {
      type: ChartTypes.FormatShortcutType.Numeric,
      format: getTextFormatProps(ChartTypes.FormatShortcutType.Numeric),
    },
  },
];

export const datePartWeekdayFormatOptions: ShortcutOption[] = [
  {
    label: 'Full name (default)',
    example: format(NOW, 'EEEE'),
    shortcut: {
      type: ChartTypes.FormatShortcutType.Automatic,
      format: {
        ...getTextFormatProps(ChartTypes.FormatShortcutType.Automatic, ChartTypes.FormatShortcutType.FullName),
      },
    },
  },
  {
    label: 'Abbreviated',
    example: format(NOW, 'EEE'),
    shortcut: {
      type: ChartTypes.FormatShortcutType.ShortName,
      format: getTextFormatProps(ChartTypes.FormatShortcutType.ShortName),
    },
  },
  {
    label: 'Numeric',
    example: format(NOW, 'e'),
    shortcut: {
      type: ChartTypes.FormatShortcutType.Numeric,
      format: getTextFormatProps(ChartTypes.FormatShortcutType.Numeric),
    },
  },
];

export const datePartDayFormatOptions: ShortcutOption[] = [
  {
    label: 'Numeric (default)',
    example: format(NOW, 'd'),
    shortcut: {
      type: ChartTypes.FormatShortcutType.Automatic,
      format: {
        ...getTextFormatProps(ChartTypes.FormatShortcutType.Automatic, ChartTypes.FormatShortcutType.Numeric),
      },
    },
  },
  {
    label: 'Ordinal',
    example: format(NOW, 'do'),
    shortcut: {
      type: ChartTypes.FormatShortcutType.Ordinal,
      format: getTextFormatProps(ChartTypes.FormatShortcutType.Ordinal),
    },
  },
];

export const datePartHourFormatOptions: ShortcutOption[] = [
  {
    label: 'Lowercase (default)',
    example: format(NOW, 'haaa'),
    shortcut: {
      type: ChartTypes.FormatShortcutType.Automatic,
      format: {
        ...getTextFormatProps(ChartTypes.FormatShortcutType.Automatic, ChartTypes.FormatShortcutType.ShortName),
      },
    },
  },
  {
    label: 'Uppercase',
    example: format(NOW, 'h aa'),
    shortcut: {
      type: ChartTypes.FormatShortcutType.FullName,
      format: getTextFormatProps(ChartTypes.FormatShortcutType.FullName),
    },
  },
  {
    label: 'Numeric',
    example: format(NOW, 'h'),
    shortcut: {
      type: ChartTypes.FormatShortcutType.Numeric,
      format: getTextFormatProps(ChartTypes.FormatShortcutType.Numeric),
    },
  },
];

export const datePartMinFormatOptions: ShortcutOption[] = [
  {
    label: 'Numeric (default)',
    example: format(NOW, 'm'),
    shortcut: {
      type: ChartTypes.FormatShortcutType.Automatic,
      format: {
        ...getTextFormatProps(ChartTypes.FormatShortcutType.Automatic, ChartTypes.FormatShortcutType.Numeric),
      },
    },
  },
  {
    label: 'Ordinal',
    example: format(NOW, 'mo'),
    shortcut: {
      type: ChartTypes.FormatShortcutType.Ordinal,
      format: getTextFormatProps(ChartTypes.FormatShortcutType.Ordinal),
    },
  },
];

export const datePartSecondFormatOptions: ShortcutOption[] = [
  {
    label: 'Numeric (default)',
    example: format(NOW, 's'),
    shortcut: {
      type: ChartTypes.FormatShortcutType.Automatic,
      format: {
        ...getTextFormatProps(ChartTypes.FormatShortcutType.Automatic, ChartTypes.FormatShortcutType.Numeric),
      },
    },
  },
  {
    label: 'Ordinal',
    example: format(NOW, 'so'),
    shortcut: {
      type: ChartTypes.FormatShortcutType.Ordinal,
      format: getTextFormatProps(ChartTypes.FormatShortcutType.Ordinal),
    },
  },
];

export const enum MenuType {
  Date = 'Date',
  Number = 'Number',
  PlainText = 'PlainText',
}

export interface ShortcutMenu {
  type: MenuType;
  options: ShortcutOption[];
  label: string;
}

const textFormatMenu: ShortcutMenu = {
  type: MenuType.PlainText,
  options: plainTextFormatOptions,
  label: 'Format',
};

const dateFormatMenu: ShortcutMenu = {
  type: MenuType.Date,
  options: dateFormatOptions,
  label: 'Date format',
};

const numberFormatMenu: ShortcutMenu = {
  type: MenuType.Number,
  options: numberFormatOptions,
  label: 'Number format',
};

export function getShortcutMenu(dataType: DataType.Name, isDiscrete: boolean, dateUnit?: DateTime.Unit): ShortcutMenu {
  switch (dataType) {
    case DataType.Name.Timestamp:
    case DataType.Name.DateTime: {
      return dateFormatMenu;
    }
    case DataType.Name.Integer:
    case DataType.Name.Decimal:
    case DataType.Name.Float: {
      if (dateUnit && isDiscrete) {
        return getDatePartShortcutMenu(dateUnit);
      }
      return numberFormatMenu;
    }
    default: {
      return textFormatMenu;
    }
  }
}

function getDatePartShortcutMenu(dateUnit?: DateTime.Unit) {
  switch (dateUnit) {
    case DateTime.Unit.Year:
      return {
        type: MenuType.PlainText,
        options: datePartYearFormatOptions,
        label: 'Year format',
      };
    case DateTime.Unit.Quarter:
      return {
        type: MenuType.PlainText,
        options: datePartQuarterFormatOptions,
        label: 'Quarter format',
      };
    case DateTime.Unit.Month:
      return {
        type: MenuType.PlainText,
        options: datePartMonthFormatOptions,
        label: 'Month format',
      };
    case DateTime.Unit.Weekday:
      return {
        type: MenuType.PlainText,
        options: datePartWeekdayFormatOptions,
        label: 'Weekday format',
      };
    case DateTime.Unit.Day:
      return {
        type: MenuType.PlainText,
        options: datePartDayFormatOptions,
        label: 'Day format',
      };
    case DateTime.Unit.Hour:
      return {
        type: MenuType.PlainText,
        options: datePartHourFormatOptions,
        label: 'Hour format',
      };
    case DateTime.Unit.Minute:
      return {
        type: MenuType.PlainText,
        options: datePartMinFormatOptions,
        label: 'Minute format',
      };
    case DateTime.Unit.Second:
      return {
        type: MenuType.PlainText,
        options: datePartSecondFormatOptions,
        label: 'Second format',
      };
  }
  return textFormatMenu;
}

export function getOptionByShortcutTypeOrFirst(
  items: ShortcutOption[],
  type: ChartTypes.FormatShortcutType
): ShortcutOption {
  return items.find((item) => item.shortcut?.type === type) ?? items[0];
}
